import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import PushToggle from "@/components/PushToggle";
import { api, API } from "@/lib/api";
import { Save, Upload, Trash2, Image as ImgIcon, User } from "lucide-react";
import { LocalVaultCard } from "@/components/LocalVault";
import { TopBar } from "@/components/PageShell";

const Field = ({ label, ...rest }) => (
  <label className="block">
    <div className="text-xs font-medium text-slate-600 mb-1">{label}</div>
    <input {...rest} className="w-full h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-slate-900 focus:border-transparent outline-none text-sm" />
  </label>
);

const POSITIONS = [
  { value: "left",   label: "Canto superior esquerdo" },
  { value: "center", label: "Centro superior" },
  { value: "right",  label: "Canto superior direito" },
];

function LogoUploader({ profile, onChange }) {
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [position, setPosition] = useState(profile.logo_position || "left");
  const fileRef = useRef(null);

  const load = async () => {
    if (!profile.logo_path) { setPreview(null); return; }
    try {
      const r = await api.get("/profile/logo", { responseType: "blob" });
      setPreview(URL.createObjectURL(r.data));
    } catch (e) { setPreview(null); }
  };
  useEffect(() => { load(); return () => { if (preview) URL.revokeObjectURL(preview); }; }, [profile.logo_path]);

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("position", position);
      const r = await api.post("/profile/logo", fd, { headers: {"Content-Type": "multipart/form-data"} });
      toast.success("Logomarca enviada");
      onChange({ ...profile, logo_path: r.data.logo_path, logo_position: r.data.logo_position });
    } catch (e) { toast.error("Falha ao enviar logo"); }
    finally { setUploading(false); }
  };

  const changePos = async (v) => {
    setPosition(v);
    await api.put("/profile", { logo_position: v });
    onChange({ ...profile, logo_position: v });
    toast.success("Posição atualizada");
  };

  const remove = async () => {
    if (!window.confirm("Remover logomarca?")) return;
    await api.delete("/profile/logo");
    onChange({ ...profile, logo_path: "" });
    setPreview(null);
    toast.success("Logo removida");
  };

  return (
    <section className="bg-white rounded-2xl border border-slate-100 p-6">
      {!profile.logo_path && (
        <div className="mb-4 rounded-xl border-2 border-[#d4a24c] bg-gradient-to-r from-amber-50 to-yellow-50 p-4 flex items-start gap-3" data-testid="profile-logo-banner">
          <div className="w-10 h-10 rounded-full bg-[#d4a24c] flex items-center justify-center shrink-0">
            <ImgIcon className="w-5 h-5 text-black"/>
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-slate-900">Suba sua logomarca para aparecer em todos os PDFs</div>
            <div className="text-xs text-slate-700 mt-0.5">
              Contratos, procurações, memórias de cálculo, publicações do DJEN — todos ganham o cabeçalho do seu escritório automaticamente.
              Sem logo, aparecerá a marca padrão.
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-lg font-semibold">Logomarca nos documentos</h2>
        {profile.logo_path && (
          <button onClick={remove} className="text-xs text-red-600 hover:underline inline-flex items-center gap-1" data-testid="btn-del-logo">
            <Trash2 className="w-3 h-3"/> Remover
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
        <div className="md:col-span-1">
          <div className="w-full h-48 rounded-xl bg-slate-50 border border-dashed border-slate-200 flex items-center justify-center overflow-hidden">
            {preview ? (
              <img src={preview} alt="logo" className="w-full h-full object-contain p-4"/>
            ) : (
              <div className="text-slate-400 text-xs text-center px-6"><ImgIcon className="w-6 h-6 mx-auto mb-2"/> Sem logo enviada. A logo padrão da Del Padre será usada.</div>
            )}
          </div>
        </div>
        <div className="md:col-span-2 space-y-4">
          <div>
            <div className="text-xs font-medium text-slate-600 mb-1">Posição no cabeçalho</div>
            <div className="grid grid-cols-3 gap-2">
              {POSITIONS.map(p => (
                <button key={p.value} onClick={() => changePos(p.value)} data-testid={`pos-${p.value}`}
                  className={`text-xs px-3 py-2 rounded-lg border transition ${position===p.value?'bg-slate-900 text-white border-slate-900':'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden
              onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} data-testid="logo-file-input"/>
            <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="btn-upload-logo"
              className="w-full h-11 rounded-xl gradient-primary text-white text-sm font-medium inline-flex items-center justify-center gap-2 hover:-translate-y-0.5 transition disabled:opacity-60">
              <Upload className="w-4 h-4"/> {uploading ? "Enviando..." : "Enviar logomarca (PNG, JPG ou WEBP)"}
            </button>
            <p className="text-xs text-slate-500 mt-2">Aparecerá em todos os documentos gerados (PDF e DOCX). Recomendado: PNG com fundo transparente, no mínimo 300×300 px.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Profile() {
  const [p, setP] = useState(null);
  const [me, setMe] = useState({ treatment: "", birth_date: "", city: "", state_code: "", state_name: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/profile").then(r => setP(r.data));
    api.get("/auth/me").then(r => setMe({
      treatment: r.data.treatment || "",
      birth_date: (r.data.birth_date || "").slice(0, 10),
      city: r.data.city || "",
      state_code: r.data.state_code || "",
      state_name: r.data.state_name || "",
    }));
  }, []);
  if (!p) return <div className="p-8 text-slate-500">Carregando...</div>;
  const set = (k) => (e) => setP({ ...p, [k]: e.target.value });
  const setMeF = (k) => (e) => setMe({ ...me, [k]: e.target.value });
  const save = async () => {
    setSaving(true);
    try {
      const hadOab = Boolean(p.oab_numero && p.oab_uf);
      const r = await api.put("/profile", p);
      setP(r.data);
      await api.patch("/me", me);
      toast.success("Perfil salvo");
      // Momento Wow: primeira vez que salva OAB completa → dispara sync inicial
      const nowHasOab = Boolean(r.data?.oab_numero && r.data?.oab_uf);
      if (nowHasOab && !r.data?.onboarding_wow_ran_at) {
        toast.info("Buscando publicações vinculadas à sua OAB…", { duration: 8000 });
        try {
          const w = await api.post("/hermes/onboarding/wow");
          const found = Number(w.data?.new_count ?? w.data?.found ?? 0);
          if (found > 0) {
            toast.success(
              `🎉 Já encontramos ${found} publicação${found > 1 ? "ões" : ""} para você!`,
              { duration: 12000 }
            );
          } else if (w.data?.status === "GEO_BLOCKED") {
            toast.warning(
              "OAB configurada. A primeira sincronização será feita pelo seu navegador em breve (CNJ só aceita IPs do Brasil).",
              { duration: 12000 }
            );
          } else {
            toast.success(
              "OAB configurada. Vamos monitorar suas publicações automaticamente todos os dias.",
              { duration: 10000 }
            );
          }
        } catch (we) { /* silent — não bloqueia o save */ }
      }
    } catch (e) { toast.error("Erro ao salvar"); }
    finally { setSaving(false); }
  };
  return (
    <div className="flex-1 min-w-0 flex flex-col" data-testid="profile-shell">
      <TopBar/>
      <div className="p-6 lg:p-8 w-full font-sans">
      <div className="text-[11px] uppercase tracking-[0.25em] font-semibold" style={{ color: "#C9953D" }}>HERMES · Identidade</div>
      <h1 className="mt-2 font-serif text-4xl md:text-5xl leading-[1.05] text-[#132238] flex items-center gap-3">
        <User className="w-8 h-8" style={{ color: "#C9953D" }}/> Meu Perfil
      </h1>
      <p className="text-slate-600 mt-2">Estes dados são usados como <b>Contratado / Outorgado</b> nos documentos.</p>

      <section className="mt-8 bg-white rounded-2xl border p-6" style={{ borderColor: "#E7E5E2" }}>
        <h2 className="font-serif text-xl mb-4 text-[#132238]">Dados profissionais</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Nome completo" value={p.nome_completo || ""} onChange={set("nome_completo")} data-testid="profile-nome" />
          <Field label="Nome do escritório" value={p.escritorio || ""} onChange={set("escritorio")} data-testid="profile-escritorio" />
          <Field label="OAB - Número" value={p.oab_numero || ""} onChange={set("oab_numero")} data-testid="profile-oab" />
          <Field label="OAB - UF" value={p.oab_uf || ""} onChange={set("oab_uf")} data-testid="profile-oab-uf" />
          <Field label="CPF" value={p.cpf || ""} onChange={set("cpf")} data-testid="profile-cpf" />
          <Field label="E-mail" value={p.email || ""} onChange={set("email")} data-testid="profile-email" />
          <Field label="Telefone / WhatsApp" value={p.telefone || ""} onChange={set("telefone")} data-testid="profile-telefone" />
        </div>
      </section>

      <div className="mt-6">
        <LogoUploader profile={p} onChange={setP}/>
      </div>

      <section className="mt-6 bg-white rounded-2xl border border-slate-100 p-6">
        <h2 className="font-heading text-lg font-semibold mb-4">Endereço profissional</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Logradouro" value={p.endereco_logradouro || ""} onChange={set("endereco_logradouro")} />
          <Field label="Número" value={p.endereco_numero || ""} onChange={set("endereco_numero")} />
          <Field label="Complemento" value={p.endereco_complemento || ""} onChange={set("endereco_complemento")} />
          <Field label="Bairro" value={p.endereco_bairro || ""} onChange={set("endereco_bairro")} />
          <Field label="Cidade" value={p.endereco_cidade || ""} onChange={set("endereco_cidade")} />
          <Field label="UF" value={p.endereco_uf || ""} onChange={set("endereco_uf")} />
          <Field label="CEP" value={p.endereco_cep || ""} onChange={set("endereco_cep")} />
        </div>
      </section>

      <section className="mt-6 bg-white rounded-2xl border border-slate-100 p-6" data-testid="profile-autopilot-section">
        <h2 className="font-heading text-lg font-semibold mb-1">Notificações do Autopilot</h2>
        <p className="text-xs text-slate-500 mb-4">
          Enquanto você dorme, o Hermes analisa o DJEN todo dia às 07:00.
          Toda manhã às 07:30, se algo importante for encontrado (sentença,
          acórdão, decisão ou prazo detectado), você recebe um e-mail digest
          com o resumo e um link direto para cada processo.
        </p>
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={me.autopilot_notifications_enabled !== false}
            onChange={e => setMe({ ...me, autopilot_notifications_enabled: e.target.checked })}
            className="w-4 h-4 accent-amber-600"
            data-testid="autopilot-notif-toggle"
          />
          <span className="text-sm text-slate-800">
            Receber e-mails do Autopilot com eventos importantes
          </span>
        </label>
        <label className="flex items-center gap-3 cursor-pointer mt-3">
          <input
            type="checkbox"
            checked={me.digest_diario_optin !== false}
            onChange={e => setMe({ ...me, digest_diario_optin: e.target.checked })}
            className="w-4 h-4 accent-amber-600"
            data-testid="digest-diario-toggle"
          />
          <span className="text-sm text-slate-800">
            Receber <strong>digest diário</strong> — resumo das publicações da noite anterior + prazos fatais de hoje
          </span>
        </label>
        {(me.digest_diario_optin !== false) && (
          <div className="ml-7 mt-2 flex items-center gap-2 text-xs">
            <span className="text-slate-600">Horário preferido:</span>
            <select
              value={String(me.digest_hour ?? 7)}
              onChange={e => setMe({ ...me, digest_hour: parseInt(e.target.value, 10) })}
              data-testid="digest-hour-select"
              className="h-8 px-2 rounded-lg border border-slate-200 text-xs bg-white"
            >
              <option value="6">06:00</option>
              <option value="7">07:00</option>
              <option value="8">08:00</option>
            </select>
            <span className="text-slate-400 text-[10px]">America/Sao_Paulo</span>
          </div>
        )}
        <label className="flex items-center gap-3 cursor-pointer mt-3">
          <input
            type="checkbox"
            checked={me.digest_semanal === true}
            onChange={e => setMe({ ...me, digest_semanal: e.target.checked })}
            className="w-4 h-4 accent-amber-600"
            data-testid="digest-semanal-toggle"
          />
          <span className="text-sm text-slate-800">
            Receber <strong>resumo semanal</strong> aos domingos 08:00 — semana passada + prazos dos próximos 7 dias
          </span>
        </label>
        <div className="mt-5 pt-5 border-t border-slate-100">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Push notifications (navegador)</div>
          <p className="text-xs text-slate-500 mb-3">
            Receba alertas instantâneos sobre novas publicações mesmo com o Hermes fechado.
            Ative uma vez por dispositivo — funciona no Chrome, Edge, Firefox e Safari 16+.
          </p>
          <PushToggle/>
          <div className="mt-4 space-y-2">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">Quais notificações quero receber:</div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={me.push_prefs?.djen !== false}
                onChange={e => setMe({ ...me, push_prefs: { ...(me.push_prefs || {}), djen: e.target.checked } })}
                className="w-4 h-4 accent-amber-600"
                data-testid="push-pref-djen"
              />
              <span className="text-sm text-slate-800">
                <strong>DJEN</strong> — novas publicações sincronizadas
              </span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={me.push_prefs?.prazos !== false}
                onChange={e => setMe({ ...me, push_prefs: { ...(me.push_prefs || {}), prazos: e.target.checked } })}
                className="w-4 h-4 accent-amber-600"
                data-testid="push-pref-prazos"
              />
              <span className="text-sm text-slate-800">
                <strong>Prazos fatais</strong> — alerta ~1h antes do vencimento
              </span>
            </label>
          </div>
        </div>
      </section>

      <section className="mt-6" data-testid="profile-vault-section">
        <h2 className="font-heading text-lg font-semibold mb-2">Cofre local do Hermes</h2>
        <p className="text-xs text-slate-500 mb-3">
          Todos os documentos e cálculos do Hermes Calc devem ser gravados
          fisicamente no seu computador, em subpastas por cliente. Configure a
          pasta raiz aqui — o servidor apaga os originais automaticamente
          após <b>5 dias</b>.
        </p>
        <LocalVaultCard />
      </section>

      <section className="mt-6 bg-white rounded-2xl border border-slate-100 p-6" data-testid="profile-downloads-section">
        <h2 className="font-heading text-lg font-semibold mb-1">Downloads e organização de arquivos</h2>
        <p className="text-xs text-slate-500 mb-4">
          Defina um prefixo (ex.: <code className="px-1.5 py-0.5 bg-slate-100 rounded">delpadre</code>) para
          organizar automaticamente seus documentos assinados no computador.
          Ex.: <span className="text-slate-700"><b>delpadre-assinado-contrato_maria.pdf</b></span>.
          Ao baixar, o navegador oferece a pasta de destino (Chrome/Edge permite lembrar uma pasta padrão em
          <i> Configurações → Downloads → Perguntar antes de fazer o download</i>).
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field
            label="Prefixo de nome (opcional, apenas letras/números/hífen)"
            value={p.download_prefix || ""}
            onChange={(e) => setP({ ...p, download_prefix: e.target.value.replace(/[^a-zA-Z0-9_-]/g, "").slice(0, 32) })}
            data-testid="profile-download-prefix" />
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Prévia do nome do arquivo</div>
            <div className="font-mono text-slate-800" data-testid="profile-download-prefix-preview">
              {(p.download_prefix ? `${p.download_prefix}-` : "") + "assinado-nome_do_documento.pdf"}
            </div>
          </div>
        </div>
      </section>

      <section className="mt-6 bg-white rounded-2xl border border-slate-100 p-6" data-testid="profile-admin-section">
        <h2 className="font-heading text-lg font-semibold mb-1">Dados pessoais</h2>
        <p className="text-xs text-slate-500 mb-4">Usados para personalizar cartões de aniversário e boas-vindas.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block">
            <div className="text-xs font-medium text-slate-600 mb-1">Tratamento</div>
            <select value={me.treatment} onChange={setMeF("treatment")} data-testid="profile-treatment"
              className="w-full h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-slate-900 outline-none text-sm">
              <option value="">—</option>
              <option value="Dr.">Dr.</option>
              <option value="Dra.">Dra.</option>
            </select>
          </label>
          <Field type="date" label="Data de nascimento" value={me.birth_date || ""} onChange={setMeF("birth_date")} data-testid="profile-birth"/>
          <Field label="Cidade" value={me.city} onChange={setMeF("city")} data-testid="profile-city"/>
          <label className="block">
            <div className="text-xs font-medium text-slate-600 mb-1">Estado (UF)</div>
            <select value={me.state_code} onChange={e=>{
              const uf = e.target.value;
              const names = {AC:"Acre",AL:"Alagoas",AP:"Amapá",AM:"Amazonas",BA:"Bahia",CE:"Ceará",DF:"Distrito Federal",ES:"Espírito Santo",GO:"Goiás",MA:"Maranhão",MT:"Mato Grosso",MS:"Mato Grosso do Sul",MG:"Minas Gerais",PA:"Pará",PB:"Paraíba",PR:"Paraná",PE:"Pernambuco",PI:"Piauí",RJ:"Rio de Janeiro",RN:"Rio Grande do Norte",RS:"Rio Grande do Sul",RO:"Rondônia",RR:"Roraima",SC:"Santa Catarina",SP:"São Paulo",SE:"Sergipe",TO:"Tocantins"};
              setMe({...me, state_code: uf, state_name: names[uf] || ""});
            }} data-testid="profile-state"
              className="w-full h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-slate-900 outline-none text-sm">
              <option value="">—</option>
              {["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"].map(uf=>
                <option key={uf} value={uf}>{uf}</option>
              )}
            </select>
          </label>
        </div>
      </section>

      <div className="mt-6 flex justify-end">
        <button data-testid="btn-save-profile" onClick={save} disabled={saving}
          className="gradient-primary text-white px-6 h-11 rounded-xl inline-flex items-center gap-2 hover:-translate-y-0.5 transition text-sm font-medium">
          <Save className="w-4 h-4" /> {saving ? "Salvando..." : "Salvar alterações"}
        </button>
      </div>
      </div>
    </div>
  );
}
